import React, {useCallback, useEffect, useMemo, useState} from "react"
import {PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {apiV2} from "@/api/axiosConfig";
import {convertV1ToV2, PaginatedResponse} from "@/types/PaginatedResponse";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";

type IncidentFeed = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    type: string;
    reference: string;
}

type IncidentFeedSelectorSimpleProps = {
    clientID?: string;
    selectedIDs?: string[];
    setSelectedIDs?: (selectedIDs: string[]) => void;
}

export function IncidentFeedSelectorSimple({clientID, selectedIDs = [], setSelectedIDs}: IncidentFeedSelectorSimpleProps) {
    // Cache feed data by ID to avoid refetching
    const [feedCache, setFeedCache] = useState<Record<string, IncidentFeed>>({})

    const headers = [
        "Name",
        "Type"
    ]

    // Clear cache when client changes
    useEffect(() => {
        setFeedCache({})
    }, [clientID])

    // Fetch selected feeds on mount to populate cache
    useEffect(() => {
        const fetchSelectedFeeds = async () => {
            if (selectedIDs.length === 0) return;

            try {
                const feedPromises = selectedIDs.map(id =>
                    apiV2.get<IncidentFeed>(`incident-feeds/${id}`)
                );
                const responses = await Promise.all(feedPromises);
                const feeds = responses.map(r => r.data);

                setFeedCache(prev => {
                    const updated = {...prev};
                    feeds.forEach(feed => {
                        updated[feed.id] = feed;
                    });
                    return updated;
                });
            } catch (error) {
                console.error("Error fetching selected feeds:", error);
            }
        };

        fetchSelectedFeeds();
    }, [selectedIDs.join(",")]); // Only re-run when the actual IDs change

    // Determine the allowed type based on selected feeds from cache
    const allowedType = useMemo(() => {
        if (selectedIDs.length === 0) return null
        // Get the first selected feed from cache
        const firstSelectedFeed = feedCache[selectedIDs[0]]
        if (!firstSelectedFeed) return null
        return firstSelectedFeed.type
    }, [selectedIDs, feedCache])

    const getIncidentFeeds = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams) => {
        const params: any = {
            search: search,
            page: page,
            limit: 10,
            ...filterParams,
        }

        if (clientID) {
            params.clientID = clientID;
        }

        const res = await apiV2.get<PaginatedResponse<IncidentFeed>>("incident-feeds", {params})

        // Cache the feeds as they're fetched
        const feeds = res.data.items || []
        setFeedCache(prev => {
            const updated = {...prev}
            feeds.forEach((feed: IncidentFeed) => {
                updated[feed.id] = feed
            })
            return updated
        })

        return convertV1ToV2(res.data, 10)
    }, [clientID])

    const formatFeedType = (type: string) => {
        if (type === "intelligence_fusion") return "Default";
        return type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    }

    const getColumns = useCallback((feed: IncidentFeed) => {
        return [
            feed.name,
            formatFeedType(feed.type)
        ]
    }, [])

    // Disable feeds that don't match the allowed type
    const isItemDisabled = useCallback((feed: IncidentFeed) => {
        if (!allowedType) return false // No type restriction if nothing selected
        return feed.type !== allowedType
    }, [allowedType])

    return <PaginatedSelector
        headers={headers}
        getItems={getIncidentFeeds}
        getColumns={getColumns}
        selectedIDs={selectedIDs}
        setSelectedIDs={setSelectedIDs}
        disableSelect={false}
        isItemDisabled={isItemDisabled}
        disableSelectAllWhenEmpty={true}
        showSelectionCount={true}
    />

}
